import { useEffect } from 'react'
import {
  useDialogControllers,
  MultiStepModal,
  Box,
  Heading,
  Text,
  TextLink,
  useResponsiveValue,
  Stack
} from '@machobear/design-system'
import { useTranslation, Trans } from 'react-i18next'
import useRole from '@/hooks/use-role'
import useProfile from '@/hooks/use-profile'
import config from '@/config'
import { VerifiedBadge } from '@/components/icons'
import useSessionStorage from '@/hooks/use-sessionstorage'
import Tip1Img from './images/Tip_1_Start_journey.png'
import Tip1ImgMobile from './images/Tip_1_Start_journey_mobile.png'
import Tip2Img from './images/Tip_2_Safety.png'
import Tip2ImgMobile from './images/Tip_2_Safety_mobile.png'
import Tip3Img from './images/Tip_3_get_Verified.png'
import Tip3ImgMobile from './images/Tip_3_get_Verified_mobile.png'
import Tip4Img from './images/Tip_4_complete_profile.png'
import Tip4ImgMobile from './images/Tip_4_complete_profile_mobile.png'
import IntroModal from './intro-modal'

const { WEB_URL } = config

const WelcomeModal = ({ session }: any) => {
  const data = useProfile()
  const { t } = useTranslation()
  const userId = session?.id
  const isTenant = useRole() === 'tenant'
  const [displayIntro] = useSessionStorage('renter-intro')

  const { visible, showModal, closeModal } = useDialogControllers({
    welcomeWizard: false,
    introModal: false
  })
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })
  const handleClose = () => {
    closeModal('welcomeWizard')()
    showModal('introModal')()
  }

  useEffect(() => {
    if (!isTenant || !session || !data?.id) return
    const introFormComplete = !!data.about_me || !!data.salary || !!data.job_title
    const hasLoggedBefore = localStorage.getItem('@session:hasLoggedBefore')
    if (userId && data.registration_complete === '1') {
      if (!introFormComplete && !hasLoggedBefore) {
        showModal('welcomeWizard')()
        localStorage.setItem('@session:hasLoggedBefore', 'true')
      } else if (!introFormComplete && displayIntro !== 'false') {
        showModal('introModal')()
      }
    }
  }, [isTenant, userId, data?.id])

  return (
    <>
      <MultiStepModal
        id="welcomeWizard"
        visible={visible.welcomeWizard}
        handleClose={handleClose}
        showButtonIcons
      >
        <MultiStepModal.Step
          headerIllustration={
            <Box
              paddingTop={{ mobile: 'small', tablet: undefined }}
              component="img"
              width="full"
              src={isMobile ? Tip1ImgMobile : Tip1Img}
            />
          }
          key={`welcome-wizard-step-0`}
          nextText={t('common.next', 'Next')}
          backText={t('common.back', 'Back')}
          nextAction={({ goNext }: { goNext: () => void }) => {
            window?.dataLayer?.push({
              event: 'app_welcomemodal_tip1_next'
            })
            goNext()
          }}
        >
          <Stack space="small">
            <Text size="small" tone="tertiary">
              {t('r.profile.view.tip.text', 'Tip')} {` 1/4`}
            </Text>
            <Box marginY="xxxsmall">
              <Heading level="6" weight="strong">
                {t('r.profile.edit.welcome_step_1.title', 'Start your liv.rent journey here!')}
              </Heading>
            </Box>
            <Box>
              <Text>
                {t(
                  'r.profile.edit.step_1.text',
                  'Your liv.rent profile is a reusable rental application. Fill in your information once, then apply to multiple listings in just a few clicks.'
                )}
              </Text>
            </Box>
          </Stack>
        </MultiStepModal.Step>
        <MultiStepModal.Step
          headerIllustration={
            <Box component="img" width="full" src={isMobile ? Tip2ImgMobile : Tip2Img} />
          }
          key={`welcome-wizard-step-1`}
          nextText={t('common.next', 'Next')}
          backText={t('common.back', 'Back')}
          nextAction={({ goNext }: { goNext: () => void }) => {
            window?.dataLayer?.push({
              event: 'app_welcomemodal_tip2_next'
            })
            goNext()
          }}
        >
          <Stack space="small">
            <Text size="small" tone="tertiary">
              {t('r.profile.view.tip.text', 'Tip')} {` 2/4`}
            </Text>
            <Box marginY="xxxsmall">
              <Heading level="6" weight="strong">
                {t('r.profile.edit.welcome_step_2.title', 'Your safety is our priority')}
              </Heading>
            </Box>
            <Box>
              <Trans i18nKey="r.profile.edit.step_2.text">
                <Text tone="secondary">
                  We are committed to never selling your data and provide{' '}
                  <TextLink href={`${WEB_URL}/privacy`} target="_blank">
                    clear guidelines
                  </TextLink>{' '}
                  as to how we use & store personal information. You can delete your profile or
                  withdraw your application at any time.
                </Text>
              </Trans>
            </Box>
          </Stack>
        </MultiStepModal.Step>
        <MultiStepModal.Step
          headerIllustration={
            <Box
              paddingTop={{ mobile: 'small', tablet: undefined }}
              component="img"
              width="full"
              src={isMobile ? Tip3ImgMobile : Tip3Img}
            />
          }
          key={`welcome-wizard-step-2`}
          nextText={t('common.next', 'Next')}
          backText={t('common.back', 'Back')}
          nextAction={({ goNext }: { goNext: () => void }) => {
            window?.dataLayer?.push({
              event: 'app_welcomemodal_tip3_next'
            })
            goNext()
          }}
        >
          <Stack space="small">
            <Text size="small" tone="tertiary">
              {t('r.profile.view.tip.text', 'Tip')} {` 3/4`}
            </Text>
            <Box alignItems="center" marginY="xxxsmall">
              <Heading level="6" weight="strong">
                {t(
                  'r.profile.edit.welcome_step_3.title',
                  'Increase your chances by 77% by getting verified'
                )}{' '}
                <VerifiedBadge
                  width={24}
                  height={24}
                  style={{ verticalAlign: isMobile ? 'middle' : 'top' }}
                />
              </Heading>
            </Box>
            <Box>
              <Trans i18nKey="r.profile.edit.step_3.text">
                <Text tone="secondary">
                  In order to create a safe, trusted rental community, we request that users{' '}
                  <TextLink
                    href="https://liv.rent/blog/livrent/rental-verification-process/"
                    target="_blank"
                  >
                    verify key information
                  </TextLink>
                  . We also manually verify landlord profiles and listings for your security and to
                  prevent rental scams.
                </Text>
              </Trans>
            </Box>
          </Stack>
        </MultiStepModal.Step>
        <MultiStepModal.Step
          headerIllustration={
            <Box component="img" width="full" src={isMobile ? Tip4ImgMobile : Tip4Img} />
          }
          key={`welcome-wizard-step-3`}
          nextText={t('b.profile.edit.get_started.button', 'Get Started')}
          backText={t('common.back', 'Back')}
          tertText={t('b.sidebar.view.browse_listings.link', 'Browse Listings')}
          tertAction={() => {
            window?.dataLayer?.push({
              event: 'app_welcomemodal_tip4_browselist'
            })
            closeModal('welcomeWizard')()
            window.open(`${WEB_URL}/rental-listings`, '_blank')
          }}
          nextAction={() => {
            window?.dataLayer?.push({
              event: 'app_welcomemodal_tip4_getstart'
            })
            handleClose()
          }}
        >
          <Stack space="small">
            <Text size="small" tone="tertiary">
              {t('r.profile.view.tip.text', 'Tip')} {` 4/4`}
            </Text>
            <Box marginY="xxxsmall">
              <Heading level="6" weight="strong">
                {t('r.profile.edit.welcome_step_4.title', 'Completing your profile')}
              </Heading>
            </Box>
            <Box>
              <Text>
                {t(
                  'r.profile.edit.step_4.text',
                  "Remember you only need to fill out your profile once you’ve found a listing you're interested in and are ready to apply."
                )}
              </Text>
            </Box>
          </Stack>
        </MultiStepModal.Step>
      </MultiStepModal>

      <IntroModal
        visible={visible.introModal}
        showModal={showModal}
        closeModal={closeModal}
        title={t('r.profile.view.intro_modal.title', 'Stand out from the crowd')}
      />
    </>
  )
}

export default WelcomeModal
